import { LayoutGrid } from "lucide-react";
import { useTranslate } from "ra-core";
import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router";

import { Button } from "@/components/ui/button";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { cn } from "@/lib/utils";

import { useInboxBadgeCount } from "../whatsapp/inbox/useInbox";
import { NavCountBadge } from "./Header";
import {
  HOME_NAV_ITEM,
  MORE_NAV_GROUPS,
  PRIMARY_NAV_ITEMS,
  SETTINGS_NAV_ITEM,
  isNavItemActive,
  navLabel,
  resolveActiveNavId,
  type NavGroup,
  type NavItem,
} from "./navigation";
import {
  useAccessibleNavGroups,
  useAccessibleNavItems,
} from "./useAccessibleNavItems";

/**
 * A phone fits five thumb targets. Home plus the three most-used workspaces
 * stay on the bar; the other three primary tabs, everything under "More" on
 * desktop, and Settings open in a bottom sheet — the same grouped list the
 * desktop dropdown shows, so muscle memory transfers between devices.
 */
const BAR_PRIMARY_IDS = new Set(["contacts", "echoes", "broadcasts"]);

const BAR_ITEMS: readonly NavItem[] = [
  HOME_NAV_ITEM,
  ...PRIMARY_NAV_ITEMS.filter((item) => BAR_PRIMARY_IDS.has(item.id)),
];

const SHEET_GROUPS: readonly NavGroup[] = [
  {
    id: "analytics",
    label: "Quality & growth",
    items: PRIMARY_NAV_ITEMS.filter((item) => !BAR_PRIMARY_IDS.has(item.id)),
  },
  ...MORE_NAV_GROUPS,
  { id: "account", label: "Account", items: [SETTINGS_NAV_ITEM] },
];

const SHEET_ITEM_IDS = new Set(
  SHEET_GROUPS.flatMap((group) => group.items.map((item) => item.id)),
);

export const MobileNavigation = () => {
  const translate = useTranslate();
  const { pathname } = useLocation();
  const barItems = useAccessibleNavItems(BAR_ITEMS);
  const activeId = resolveActiveNavId(pathname);
  const sheetActive = activeId != null && SHEET_ITEM_IDS.has(activeId);

  // iOS PWA: the viewport ignores the bottom safe-area inset, so pad the bar
  // by hand to keep it clear of the home indicator.
  const { isPwa, isWebiOS } = useMemo(
    () => ({
      isPwa: window.matchMedia("(display-mode: standalone)").matches,
      isWebiOS: /iPad|iPod|iPhone/.test(window.navigator.userAgent),
    }),
    [],
  );
  const safeAreaPadding = isPwa && isWebiOS ? 15 : 0;

  return (
    <nav
      aria-label={translate("crm.navigation.label")}
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-border/60 bg-secondary"
      style={{
        paddingBottom: safeAreaPadding || undefined,
        height: `calc(var(--spacing) * 14 + ${safeAreaPadding}px)`,
      }}
    >
      <div className="flex h-14 items-stretch justify-around px-1">
        {barItems.map((item) => (
          <NavigationButton
            key={item.id}
            href={item.to}
            Icon={item.icon}
            label={navLabel(item, translate)}
            isActive={isNavItemActive(item, pathname)}
          />
        ))}
        <MoreSheet isActive={sheetActive} />
      </div>
    </nav>
  );
};

const buttonClassName = (isActive: boolean) =>
  cn(
    "relative h-auto w-16 flex-col gap-1 rounded-md px-1 py-2",
    isActive ? "text-secondary-foreground" : "text-muted-foreground",
  );

const NavigationButton = ({
  href,
  Icon,
  label,
  isActive,
}: {
  href: string;
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  label: string;
  isActive: boolean;
}) => (
  <Button asChild variant="ghost" className={buttonClassName(isActive)}>
    <Link to={href} aria-current={isActive ? "page" : undefined}>
      <Icon className="size-6" />
      <span className="text-[0.6rem] font-medium">{label}</span>
    </Link>
  </Button>
);

const MoreSheet = ({ isActive }: { isActive: boolean }) => {
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  const groups = useAccessibleNavGroups(SHEET_GROUPS);
  const inboxCount = useInboxBadgeCount();
  const label = translate("crm.navigation.more", { _: "More" });

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button
          variant="ghost"
          className={buttonClassName(isActive)}
          aria-current={isActive ? "page" : undefined}
        >
          <LayoutGrid className="size-6" />
          <span className="text-[0.6rem] font-medium">{label}</span>
          <NavCountBadge
            count={inboxCount}
            className="absolute right-1 top-1 min-w-4 px-1 text-[10px] leading-4"
          />
        </Button>
      </DrawerTrigger>
      <DrawerContent className="max-h-[85dvh]">
        <DrawerHeader className="text-left">
          <DrawerTitle>{label}</DrawerTitle>
          <DrawerDescription>
            {translate("crm.navigation.more_description", {
              _: "Every other workspace, grouped by team.",
            })}
          </DrawerDescription>
        </DrawerHeader>
        <div className="space-y-5 overflow-y-auto px-4 pb-8">
          {groups.map((group) => (
            <section key={group.id} aria-label={group.label}>
              <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {group.label}
              </h3>
              <div className="grid grid-cols-3 gap-2">
                {group.items.map((item) => (
                  <SheetTile
                    key={item.id}
                    item={item}
                    badge={item.badge === "inbox" ? inboxCount : null}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

const SheetTile = ({
  item,
  badge,
}: {
  item: NavItem;
  badge: number | null;
}) => {
  const translate = useTranslate();
  const { pathname } = useLocation();
  const isActive = isNavItemActive(item, pathname);
  const Icon = item.icon;
  return (
    <DrawerClose asChild>
      <Link
        to={item.to}
        aria-current={isActive ? "page" : undefined}
        className={cn(
          "relative flex flex-col items-center gap-1.5 rounded-lg border px-2 py-3 text-center text-xs font-medium",
          isActive
            ? "border-primary/40 bg-accent text-accent-foreground"
            : "border-border/60 text-foreground/90 hover:bg-accent/60",
        )}
      >
        <Icon className="size-5 text-muted-foreground" aria-hidden="true" />
        <span className="leading-tight">{navLabel(item, translate)}</span>
        <NavCountBadge
          count={badge}
          className="absolute right-1.5 top-1.5 min-w-4 px-1 text-[10px] leading-4"
        />
      </Link>
    </DrawerClose>
  );
};
